import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import SyncProblemOutlined from "@mui/icons-material/SyncProblemOutlined";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { AnimatedNumber } from "../../../../../components/UIToolkit/Animations/AnimatedNumber";
import MetaChip from "../../../../../components/UIToolkit/Badges/MetaChip";
import { SystemLinearMeter } from "../../../../../components/UIToolkit/DataViz/SystemLinearMeter/SystemLinearMeter";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import { ALPHA, FONT_SIZE, MOTION, RADIUS } from "../../../../../theme/tokens";
import { AirtableDiffGrid } from "./AirtableDiffGrid";
import { AIRTABLE_KIND_LABEL_KEY, AIRTABLE_KIND_TONE } from "./airtableImportLabels";
import type { AirtableRowKind } from "./airtableImportTypes";
import type { AirtableReviewState } from "./useAirtableReview";

type Props = {
	review: AirtableReviewState;
	/** The form has unsaved edits — the diff was computed against the SAVED config, so say so. */
	isDirty: boolean;
	/** Set when a verify refused because the Airtable record moved; the grid below is the re-read diff. */
	staleRecordMessage: string | null;
	/** A request-level failure on this step (the preview itself, a non-refusal verify error, or a refused save). */
	errorMessage: string | null;
	/** Already-translated reasons the save itself refused, listed under `errorMessage` when present. */
	saveBlockedReasons?: string[];
	/** The facility's OTHER intake record ids. Informational — never blocks Apply. */
	duplicateRecordIds?: string[];
	/**
	 * Whether that list is partial. The banner then says "and more" rather than implying it is whole —
	 * there is no exact total on the wire on purpose, because past one Airtable page it would be a
	 * guess, and the remedy (go look at the base) is the same either way.
	 */
	duplicatesTruncated?: boolean;
	onCheckedChange: (rowId: string, checked: boolean) => void;
	disabled: boolean;
};

/** Kinds shown in the summary chips, in the order they matter to a reviewer. */
const SUMMARY_KINDS: AirtableRowKind[] = ["deterministic", "llm", "out_of_band", "unresolvable", "protected", "blocked"];

/**
 * Step 3 — the diff, and the one screen where the operator actually decides anything.
 *
 * The acceptance ledger leads deliberately. Everything else here is a table of forty near-identical
 * rows; without a figure at the top, "how far through this am I, and how much of it is a model's guess"
 * is only answerable by scrolling. The meter fills as rows are accepted, so the progress of a
 * necessarily row-by-row task is at least visible.
 */
export const AirtableReviewStep: FC<Props> = ({
	review,
	isDirty,
	staleRecordMessage,
	errorMessage,
	saveBlockedReasons = [],
	duplicateRecordIds = [],
	duplicatesTruncated = false,
	onCheckedChange,
	disabled,
}) => {
	const { t } = useTranslation();
	const { entries, countsByKind, acceptableCount, acceptedCount } = review;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
			<Box
				data-testid="airtable-review-ledger"
				sx={(theme) => ({
					display: "flex",
					// Stacks below `sm`: side by side, the 220px figure column leaves the chips ~110px, which
					// ellipsizes every one of them into "2 Direc…" — a count strip that no longer names anything.
					flexDirection: { xs: "column", sm: "row" },
					flexWrap: "wrap",
					alignItems: { xs: "stretch", sm: "center" },
					gap: { xs: 1.5, md: 3 },
					p: 1.75,
					borderRadius: `${RADIUS.CARD}px`,
					// A brand wash rather than a white card: the ledger is the step's one focal surface and
					// has to read as a different plane from the grid under it, without becoming a callout.
					background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, ALPHA.HERO_WASH)} 0%, ${alpha(
						theme.palette.primary.main,
						ALPHA.HOVER_OVERLAY,
					)} 100%)`,
					border: `1px solid ${alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY)}`,
					// The solid left accent rule is what makes the band read as brand-OWNED rather than as a
					// gray card: the wash alone is deliberately below `HINT_TINT` (it has to sit under a
					// figure, not shout), and at that strength it needs one saturated edge to register. Same
					// idiom as `CoverageSectionHeader`'s roll-up band.
					borderLeft: `4px solid ${theme.palette.primary.main}`,
				})}
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: { xs: 0, sm: 220 }, flexShrink: 0 }}>
					<Typography
						component="span"
						sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }}
					>
						{t("department.config.airtable.review.acceptedLabel")}
					</Typography>
					<Typography
						component="span"
						data-testid="airtable-review-count"
						sx={{ fontSize: FONT_SIZE.TITLE_XL, fontWeight: 700, color: "primary.main", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}
					>
						<AnimatedNumber value={acceptedCount} />
						<Box component="span" sx={{ color: "text.secondary", fontWeight: 600, fontSize: FONT_SIZE.TITLE_MD }}>
							{` / ${acceptableCount}`}
						</Box>
					</Typography>
					<SystemLinearMeter
						value={acceptedCount}
						max={Math.max(acceptableCount, 1)}
						height={6}
						ariaLabel={t("department.config.airtable.review.meter.ariaLabel", { accepted: acceptedCount, total: acceptableCount })}
						dataTestId="airtable-review-meter"
					/>
				</Box>
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, flex: 1, minWidth: 0 }}>
					{SUMMARY_KINDS.filter((kind) => countsByKind[kind] > 0).map((kind) => (
						<MetaChip
							key={kind}
							label={`${countsByKind[kind]} ${t(AIRTABLE_KIND_LABEL_KEY[kind])}`}
							tone={AIRTABLE_KIND_TONE[kind]}
							size="medium"
							dataTestId={`airtable-summary-${kind}`}
						/>
					))}
				</Box>
			</Box>

			{staleRecordMessage && (
				<SystemBanner
					tone="error"
					icon={<SyncProblemOutlined />}
					title={t("department.config.airtable.stale.title")}
					description={staleRecordMessage}
					dataTestId="airtable-stale-banner"
				/>
			)}
			{errorMessage && !staleRecordMessage && (
				<SystemBanner
					tone="error"
					title={t("department.config.airtable.error.title")}
					// `div`, not the default `p`: a <ul> inside a <p> is invalid nesting and the browser
					// silently closes the paragraph around it (same trap `SystemBanner` documents).
					descriptionComponent="div"
					description={
						<>
							{errorMessage}
							{saveBlockedReasons.length > 0 && (
								<Box component="ul" sx={{ m: "4px 0 0", pl: 2.5 }} data-testid="airtable-save-blocked-reasons">
									{saveBlockedReasons.map((reason) => (
										<li key={reason}>{reason}</li>
									))}
								</Box>
							)}
						</>
					}
					dataTestId="airtable-error-banner"
				/>
			)}
			{duplicateRecordIds.length > 0 && (
				// INFO, not warning or error: the operator already chose between these rows in step 1, where
				// each was shown with its kickoff title. This only exists so somebody who picked the wrong
				// one of two near-identical rows can notice before applying. It never blocks Apply.
				<SystemBanner
					tone="info"
					icon={<ContentCopyOutlined />}
					title={t("department.config.airtable.duplicates.title", { count: duplicateRecordIds.length })}
					description={
						duplicatesTruncated
							? t("department.config.airtable.duplicates.truncated", { shown: duplicateRecordIds.length, recordIds: duplicateRecordIds.join(", ") })
							: t("airtable.import.multiple.records", { count: duplicateRecordIds.length, recordIds: duplicateRecordIds.join(", ") })
					}
					dataTestId="airtable-duplicates-banner"
				/>
			)}
			{isDirty && (
				// Warn, never block: the diff was computed against the SAVED config, so a field the operator
				// has already edited by hand will show its saved value on the left — and accepting the row
				// overwrites their edit. Blocking would be worse; they may well want exactly that.
				<SystemBanner
					tone="warning"
					icon={<EditNoteOutlined />}
					title={t("department.config.airtable.dirty.title")}
					description={t("department.config.airtable.dirty.body")}
					dataTestId="airtable-dirty-banner"
				/>
			)}

			{entries.length === 0 ? (
				<EmptyState
					title={t("department.config.airtable.review.empty.title")}
					hint={t("department.config.airtable.review.empty.hint")}
					dataTestId="airtable-review-empty"
				/>
			) : (
				<Box
					sx={{
						minWidth: 0,
						// Enter-only, transform/opacity: the grid replaces itself wholesale when a refused
						// apply re-reads the record, and a silent swap of forty rows reads as nothing happening.
						"@keyframes airtableDiffEnter": { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "translateY(0)" } },
						animation: `airtableDiffEnter ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { animation: "none" },
					}}
				>
					<AirtableDiffGrid entries={entries} onCheckedChange={onCheckedChange} disabled={disabled} />
				</Box>
			)}
		</Box>
	);
};
